import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '@/api/endpoints';
import { useApi } from '@/api/useApi';
import { useAuth } from '@/auth/AuthContext';
import { LoadingState, EmptyState } from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { CodeField, codePayload, type CodeValue } from '@/components/CodeField';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatNumber } from '@/i18n/format';
import type { CreateWarehouseResult } from '@/api/types';
import { SYSTEM_KIND_META, TIME_ZONES, describeSetupError, newKey } from './setup';

/**
 * ADR-083 — Warehouses (Ops). The list every member with REFERENCE.READ can
 * see (a warehouse-scoped member sees its own warehouse only - the server
 * filters), and "Create warehouse" for an organisation-wide ADMIN. The path
 * the partner asked for: Warehouses -> Create -> Set up zones -> Add cells.
 */
export function WarehousesListPage(): JSX.Element {
  const { t, locale } = useLanguage();
  const { state, hasPermission } = useAuth();
  const result = useApi(() => endpoints.warehouseSetup(), []);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<(CreateWarehouseResult & { name: string }) | null>(null);
  const scoped = state.status === 'authenticated' && state.self.warehouse_id !== null;
  const canCreate = hasPermission('ORG.WRITE') && !scoped;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('nav.warehouses')}</h1>
          <div className="sub">{t('warehouses.subtitle')}</div>
        </div>
        {canCreate && !creating && (
          <button type="button" className="primary" onClick={() => { setCreated(null); setCreating(true); }}>
            {t('warehouses.create')}
          </button>
        )}
      </div>
      {hasPermission('ORG.WRITE') && scoped && <p className="notice">{t('warehouses.orgAdminOnly')}</p>}

      {created && (
        <div className="card success-card" role="status">
          <h2 className="card-title">{t('warehouses.createdTitle', { name: created.name })}</h2>
          <p>{t('warehouses.createdBody', { code: created.code })}</p>
          <div className="form-actions">
            <Link className="button-link" to={`/warehouses/${created.warehouseId}`}>
              {t('warehouses.nextSetupZones')}
            </Link>
          </div>
        </div>
      )}

      {creating && (
        <CreateWarehouseForm
          onCancel={() => setCreating(false)}
          onCreated={(r) => {
            setCreating(false);
            setCreated(r);
            result.reload();
          }}
        />
      )}

      {result.status === 'loading' && <LoadingState />}
      {result.status === 'error' && <ErrorBanner error={result.error} onRetry={result.reload} />}
      {result.status === 'success' &&
        (result.data.warehouses.length === 0 ? (
          <EmptyState title={t('warehouses.empty')} />
        ) : (
          <ul className="warehouse-list">
            {result.data.warehouses.map((w) => (
              <li key={w.id} className="card">
                <Link to={`/warehouses/${w.id}`} className="warehouse-link">
                  <span className="warehouse-name">{w.name}</span>
                  <span className="badge neutral">{w.code}</span>
                </Link>
                <div className="sub">
                  {w.legal_entity_name} · {w.time_zone}
                </div>
                {w.system_places && (
                  <div className="meta" data-testid="system-places">
                    {t('warehouses.systemPlacesLine', {
                      list: w.system_places.map((p) => `${t(SYSTEM_KIND_META[p.kind].label)}: ${p.code ?? t('warehouses.missing').toLowerCase()}`).join(' · '),
                    })}
                  </div>
                )}
                <div className="warehouse-facts">
                  <span>{t('warehouses.zonesCount', { n: formatNumber(w.zone_count, locale) })}</span>
                  <span>{t('warehouses.cellsCount', { n: formatNumber(w.cell_count, locale) })}</span>
                  {w.missing_system_kinds.length === 0 ? (
                    <span className="badge ok">{t('warehouses.ready')}</span>
                  ) : (
                    <span className="badge warn">{t('warehouses.notReady')}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
}

function CreateWarehouseForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (r: CreateWarehouseResult & { name: string }) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const legalEntities = useApi(() => endpoints.legalEntities(), []);
  const browserTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'Europe/Riga';
    }
  }, []);
  const zones = useMemo(() => [...new Set([browserTz, ...TIME_ZONES])], [browserTz]);
  const [name, setName] = useState('');
  const [code, setCode] = useState<CodeValue>({ code: '', auto: true });
  const [legalEntityId, setLegalEntityId] = useState('');
  const [timeZone, setTimeZone] = useState(browserTz);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const key = useRef(newKey());

  const entities = legalEntities.status === 'success' ? legalEntities.data.legalEntities : [];
  const chosenLe = legalEntityId || (entities.length === 1 ? entities[0]!.id : '');

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const r = await endpoints.createWarehouse({
        legalEntityId: chosenLe,
        name: name.trim(),
        timeZone,
        ...codePayload(code),
        idempotencyKey: key.current,
        withSystemLocations: true,
      });
      key.current = newKey();
      onCreated({ ...r, name: name.trim() });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const errText = error ? describeSetupError(t, error) : null;
  return (
    <form
      className="card wh-form"
      aria-labelledby="wh-create-title"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h2 id="wh-create-title" className="card-title">{t('warehouses.createTitle')}</h2>
      {legalEntities.status === 'success' && entities.length === 0 && (
        <p className="notice">
          {t('warehouses.noLegalEntity')} <Link to="/onboarding">{t('nav.onboarding')}</Link>
        </p>
      )}
      <div className="form-grid">
        <div className="field-row">
          <label htmlFor="wh-new-name">{t('warehouses.name')}</label>
          <input id="wh-new-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} disabled={busy} placeholder={t('warehouses.namePlaceholder')} />
        </div>
        <CodeField entity="WAREHOUSE" name={name} value={code} onChange={setCode} disabled={busy} id="wh-new-code" />
        {entities.length > 1 && (
          <div className="field-row">
            <label htmlFor="wh-new-le">{t('billing.legalEntity')}</label>
            <select id="wh-new-le" value={legalEntityId} onChange={(e) => setLegalEntityId(e.target.value)} required disabled={busy}>
              <option value="" disabled>{t('billing.selectLegalEntity')}</option>
              {entities.map((le) => (
                <option key={le.id} value={le.id}>
                  {le.name}
                  {le.country ? ` (${le.country})` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="field-row">
          <label htmlFor="wh-new-tz">{t('warehouses.timeZone')}</label>
          <select id="wh-new-tz" value={timeZone} onChange={(e) => setTimeZone(e.target.value)} disabled={busy} aria-describedby="wh-new-tz-hint">
            {zones.map((z) => (
              <option key={z} value={z}>{z}</option>
            ))}
          </select>
          <p id="wh-new-tz-hint" className="field-hint">{t('warehouses.timeZoneHint')}</p>
        </div>
      </div>
      <p className="field-hint">{t('warehouses.systemLocationsNote')}</p>
      {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
      <div className="form-actions">
        <button type="submit" className="primary" disabled={busy || name.trim() === '' || !chosenLe}>
          {busy ? t('warehouses.creating') : t('warehouses.createSubmit')}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
